import { buildFetchConfig, fetchJsonOrFallback } from './fetchUtils';

export interface TncStatusResponse {
  tnc_up_to_date: boolean;
  current_version: string;
  previously_accepted_version: string | null;
}

interface TncSubmitResponse {
  success: boolean;
  tnc_up_to_date: boolean;
  current_version: string;
  timestamp: string;
}

/**
 * Fetch the current user's T&C acceptance status
 */
const TNC_FETCH_TIMEOUT_MS = 15_000;

export const fetchTncStatus = async (): Promise<TncStatusResponse> => {
  const { baseURL, headers } = buildFetchConfig();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TNC_FETCH_TIMEOUT_MS);

  try {
    return await fetchJsonOrFallback(
      `${baseURL}/user/tnc/status`,
      { headers, signal: controller.signal },
      {
        tnc_up_to_date: false,
        current_version: '',
        previously_accepted_version: null,
      },
      {
        fail: response =>
          `Failed to fetch T&C status: ${response.status} ${response.statusText}`,
        error: 'Error fetching T&C status (network failure)',
      },
    );
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Submit T&C acceptance or rejection
 */
export const submitTnc = async (
  accepted: boolean,
): Promise<TncSubmitResponse | null> => {
  const { baseURL, headers } = buildFetchConfig();

  return fetchJsonOrFallback(
    `${baseURL}/user/tnc`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        accepted,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    },
    null,
    {
      fail: response =>
        `Failed to submit T&C: ${response.status} ${response.statusText}`,
      error: 'Error submitting T&C (network failure)',
    },
  );
};
